// Functionality for Free Gift & Sample Cart Automation for Anniversary Sale start =====
(function () {
  const settingsEl = document.getElementById("free-gift-settings");

  const enableAnniversary = settingsEl?.dataset.enableAnniversary === "true";
  const enableSample = settingsEl?.dataset.enableSample === "true";

  const variantId1 = enableAnniversary
    ? Number(settingsEl.dataset.anniversaryId)
    : null;
  const variantId2 = enableSample ? Number(settingsEl.dataset.sampleId) : null;

  const variantId1Available =
    settingsEl?.dataset.anniversaryAvailability === "true";
  const variantId2Available = settingsEl?.dataset.sampleAvailability === "true";

  const insuranceVariant = settingsEl?.dataset.insuranceId
    ? Number(settingsEl.dataset.insuranceId)
    : null;

  const anniversaryThreshold =
    Number(settingsEl?.dataset.anniversaryThreshold) || 299;
  const sampleThreshold = Number(settingsEl?.dataset.sampleThreshold) || 1;

  // --- Track cart edits and writes so checkout never races a pending save ---
  // cart.js debounces quantity and note saves, so a fast click on Checkout could otherwise
  // validate (and redirect with) stale cart data.
  const EDIT_DEBOUNCE_MS =
    (typeof ON_CHANGE_DEBOUNCE_TIMER === "number"
      ? ON_CHANGE_DEBOUNCE_TIMER
      : 300) + 50;
  let lastCartEditAt = 0;
  const pendingCartWrites = new Set();

  // Edits the shopper made that may not have reached the server yet.
  // cart.js uses ONE debounced handler for the whole cart, so editing line A and then line B
  // within the debounce window cancels A's save. We remember every edit until its save is confirmed.
  const unsavedQuantities = new Map(); // "<line>:<variantId>" -> { line, variantId, quantity }
  let unsavedNote = null; // { value }

  ["input", "change"].forEach((type) =>
    document.addEventListener(
      type,
      (e) => {
        const target = e.target;
        if (!target?.closest?.("cart-items, cart-drawer-items, cart-note"))
          return;
        lastCartEditAt = Date.now();

        if (
          e.type === "change" &&
          target.matches?.("input[data-index][data-quantity-variant-id]")
        ) {
          const quantity = parseInt(target.value, 10);
          if (!isNaN(quantity) && quantity >= 0) {
            const line = Number(target.dataset.index);
            const variantId = String(target.dataset.quantityVariantId);
            unsavedQuantities.set(`${line}:${variantId}`, {
              line,
              variantId,
              quantity,
            });
          }
        } else if (
          typeof target.value === "string" &&
          target.closest("cart-note")
        ) {
          unsavedNote = { value: target.value };
        }
      },
      true,
    ),
  );

  function parseRequestBody(init) {
    try {
      return typeof init?.body === "string" ? JSON.parse(init.body) : null;
    } catch (err) {
      return null;
    }
  }

  // Called when a tracked cart write got a response from the server
  function onCartWriteSettled(url, body, res) {
    if (!body) return;
    if (/\/cart\/change/.test(url) && body.line != null) {
      // A 2xx/4xx answer means cart.js handled this line itself (a 4xx such as a stock limit makes
      // it reset the input to the real quantity). A 5xx/429 means the save did not happen: keep the edit.
      const handled = res && res.status < 500 && res.status !== 429;
      if (handled) {
        for (const [key, rec] of unsavedQuantities) {
          if (rec.line === Number(body.line)) unsavedQuantities.delete(key);
        }
      }
    } else if (/\/cart\/update/.test(url) && typeof body.note === "string") {
      if (res?.ok && unsavedNote && unsavedNote.value === body.note) {
        unsavedNote = null; // only a successful save clears a note edit
      }
    }
  }

  const nativeFetch = window.fetch.bind(window);
  window.fetch = function (input, init) {
    const request = nativeFetch(input, init);
    const url = typeof input === "string" ? input : input?.url || "";
    if (/\/cart\/(add|change|update)(\.js)?(\?|$)/.test(url)) {
      pendingCartWrites.add(request);
      const body = parseRequestBody(init);
      request.then(
        (res) => {
          pendingCartWrites.delete(request);
          onCartWriteSettled(url, body, res);
        },
        () => pendingCartWrites.delete(request),
      );
    }
    return request;
  };

  // Resolves once no cart edit is waiting on cart.js's debounce and no cart write is in flight.
  // It repeats whenever the shopper edits again while we wait, so it can't resolve too early.
  async function flushPendingCartEdits() {
    for (let attempt = 0; attempt < 20; attempt++) {
      const editAt = lastCartEditAt;
      const wait = Math.max(0, editAt + EDIT_DEBOUNCE_MS - Date.now());
      await new Promise((resolve) => setTimeout(resolve, wait));
      await Promise.allSettled([...pendingCartWrites]);
      if (editAt === lastCartEditAt && pendingCartWrites.size === 0) return;
    }
    throw new Error("Cart edits did not settle");
  }

  function postCartWrite(path, payload) {
    return fetch(`${window.Shopify.routes.root}cart/${path}.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).then((res) => {
      if (!res.ok) throw new Error(`Cart ${path} failed (${res.status})`);
      return res;
    });
  }

  // Compares the shopper's edits with the real cart and re-saves anything that did not make it.
  // Throws when a save fails, so checkout is blocked instead of continuing with an old value.
  async function saveUnsavedEdits() {
    if (!unsavedQuantities.size && unsavedNote === null) return;

    const cart = await fetchCart();
    let wrote = false;

    // Line items are resolved from this one snapshot and written by key, so a removal
    // earlier in the loop can't shift the lines of the later ones.
    for (const [key, rec] of [...unsavedQuantities]) {
      const item = cart.items[rec.line - 1];
      unsavedQuantities.delete(key);
      if (
        !item ||
        String(item.variant_id) !== rec.variantId ||
        isFreebie(item.variant_id) // freebie quantities are owned by syncCart
      ) {
        continue;
      }
      if (item.quantity !== rec.quantity) {
        await postCartWrite("change", { id: item.key, quantity: rec.quantity });
        wrote = true;
      }
    }

    if (unsavedNote !== null) {
      if ((cart.note || "") !== unsavedNote.value) {
        await postCartWrite("update", { note: unsavedNote.value });
        wrote = true;
      }
      unsavedNote = null;
    }

    if (wrote) updateCartSections(await fetchCart());
  }

  function toggleCheckoutButtons(disabled) {
    const checkoutButtons = document.querySelectorAll(
      "#CartDrawer-Checkout, #checkout.cart__checkout-button",
    );

    checkoutButtons.forEach((btn) => {
      if (disabled) {
        btn.setAttribute("disabled", "disabled");
        btn.classList.add("disabled");
      } else {
        btn.removeAttribute("disabled");
        btn.classList.remove("disabled");
      }
    });
  }

  let loadingMessageTimeout;

  function toggleLoadingMessage(show = true, minDuration = 1000) {
    if (!variantId1Available && !variantId2Available) return;

    const msg = document.getElementById("freebie-loading-message");
    // console.log(show ? "showLoadingMessage" : "hideLoadingMessage", msg);
    if (!msg) return;

    clearTimeout(loadingMessageTimeout);

    if (show) {
      msg.classList.remove("hidden");
      const spinner = msg.querySelector(".loading__spinner");
      if (spinner) spinner.classList.remove("hidden");
    } else {
      // hide only after minDuration to avoid flicker
      loadingMessageTimeout = setTimeout(() => {
        msg.classList.add("hidden");
        const spinner = msg.querySelector(".loading__spinner");
        if (spinner) spinner.classList.add("hidden");
      }, minDuration);
    }
  }

  function checkLocalCartState() {
    // Detect which cart context is present
    const isCartPage = document.querySelector("#main-cart-items");
    const isCartDrawer = document.querySelector(".cart-items");

    let cartItemsEls = [];

    if (isCartPage) {
      // Cart page
      cartItemsEls = Array.from(
        document.querySelectorAll(
          "#main-cart-items .cart-item [data-product-id]",
        ),
      ).filter((el) => !el.closest(".cart-item__quantity_mobile"));
    } else if (isCartDrawer) {
      // Cart drawer
      cartItemsEls = document.querySelectorAll(".cart-items [data-variant-id]");
    }

    // console.log("[checkLocalCartState] Found cart items:", cartItemsEls.length);
    if (!cartItemsEls.length) return;

    // Normalize all IDs to numbers
    const cartVariantIds = Array.from(cartItemsEls).map((el) =>
      Number(el.dataset.variantId || el.dataset.productId),
    );

    const freebieVariants = [variantId1, variantId2].filter(Boolean);

    const onlyFreebies =
      cartVariantIds.length > 0 &&
      cartVariantIds.every((id) => freebieVariants.includes(id));

    const onlyFreebiesAndInsurance =
      cartVariantIds.length > 0 &&
      cartVariantIds.every((id) =>
        [...freebieVariants, insuranceVariant].includes(id),
      );

    const hasFreebies = cartVariantIds.some((id) =>
      freebieVariants.includes(id),
    );

    // --- Show/hide loading message ---
    toggleLoadingMessage(hasFreebies);

    // Disable checkout instantly if cart has only freebies or freebies + insurance
    if (onlyFreebies || onlyFreebiesAndInsurance) {
      toggleCheckoutButtons(true);
    }

    // Disable checkout if no freebies are present yet
    // if (!hasFreebies) {
    // toggleCheckoutButtons(true);
    // console.log(
    //   "[checkLocalCartState] No freebies detected, checkout disabled",
    // );
    // }
  }

  // --- Sync state: while a freebie correction is in flight, checkout stays locked ---
  let syncInFlight = null;
  let syncQueued = false;

  const isFreebie = (id) =>
    [variantId1, variantId2].filter(Boolean).includes(Number(id));

  // Returns true when the cart violates a freebie rule (qty > 1)
  function hasFreebieOverLimit(cart) {
    return cart.items.some(
      (item) => isFreebie(item.variant_id) && item.quantity > 1,
    );
  }

  // Builds the /cart/update.js payload needed to make the cart compliant
  function getFreebieUpdates(cart) {
    const preDiscountSubtotal =
      cart.items.reduce((sum, item) => sum + item.original_line_price, 0) / 100;

    const hasVariant1 = cart.items.some(
      (item) => item.variant_id === variantId1,
    );
    const hasVariant2 = cart.items.some(
      (item) => item.variant_id === variantId2,
    );
    const updates = {};

    // --- Ensure freebies can't exceed quantity 1 ---
    // Sums quantities per variant, so split lines (e.g. different properties) are caught too
    const qtyByVariant = {};
    cart.items.forEach((item) => {
      if (isFreebie(item.variant_id)) {
        qtyByVariant[item.variant_id] =
          (qtyByVariant[item.variant_id] || 0) + item.quantity;
      }
    });
    Object.keys(qtyByVariant).forEach((id) => {
      if (qtyByVariant[id] > 1) updates[id] = 1;
    });

    const freebieVariants = [variantId1, variantId2].filter(Boolean);
    const cartVariantIds = cart.items.map((item) => item.variant_id);

    const onlyFreebies =
      cartVariantIds.length > 0 &&
      cartVariantIds.every((id) => freebieVariants.includes(id));

    const onlyFreebiesAndInsurance =
      cartVariantIds.length > 0 &&
      cartVariantIds.every((id) =>
        [...freebieVariants, insuranceVariant].includes(id),
      );

    // Remove freebies if they're the only items (or only with insurance)
    if (onlyFreebies || onlyFreebiesAndInsurance) {
      freebieVariants.forEach((id) => {
        if (cartVariantIds.includes(id)) updates[id] = 0;
      });
    }

    const freebiesTotal = cart.items
      .filter((item) => isFreebie(item.variant_id))
      .reduce((sum, item) => sum + item.original_line_price / 100, 0);

    // --- Threshold-based logic ---
    if (!onlyFreebies) {
      if (preDiscountSubtotal <= freebiesTotal && freebiesTotal > 0) {
        if (hasVariant1) updates[variantId1] = 0;
        if (hasVariant2) updates[variantId2] = 0;
      } else {
        // Threshold is checked against the subtotal WITHOUT the freebies themselves,
        // so an extra freebie unit can't push the cart over a threshold
        const subtotalWithoutFreebies = preDiscountSubtotal - freebiesTotal;

        if (variantId1) {
          if (subtotalWithoutFreebies >= anniversaryThreshold) {
            if (!hasVariant1 && variantId1Available) updates[variantId1] = 1;
          } else if (hasVariant1) {
            updates[variantId1] = 0;
          }
        }

        if (variantId2) {
          if (subtotalWithoutFreebies >= sampleThreshold) {
            if (!hasVariant2 && variantId2Available) updates[variantId2] = 1;
          } else if (hasVariant2) {
            updates[variantId2] = 0;
          }
        }
      }
    }

    // Convert corrections for variants already in the cart into line-item-key updates.
    // /cart/update.js applies a variant-ID key to the FIRST matching line only, so when a freebie
    // is split across lines (e.g. different properties) we keep the allowed quantity on the first
    // line(s) and trim or remove the rest. Variant IDs are kept only for new additions.
    for (const id of Object.keys(updates)) {
      if (!id || id === "null" || isNaN(Number(id))) {
        delete updates[id];
        continue;
      }
      const matchingItems = cart.items.filter(
        (item) => String(item.variant_id) === id,
      );
      if (!matchingItems.length) continue; // new addition: keep the variant ID
      let remaining = updates[id];
      delete updates[id];
      matchingItems.forEach((item) => {
        const quantity = Math.min(item.quantity, remaining);
        remaining -= quantity;
        if (quantity !== item.quantity) updates[item.key] = quantity;
      });
    }

    return { updates, onlyFreebies, onlyFreebiesAndInsurance };
  }

  function fetchCart() {
    return fetch(`${routes.cart_url}.js`, { cache: "no-store" }).then((res) =>
      res.json(),
    );
  }

  // Fetches the cart, applies any freebie corrections and resolves with the final cart.
  // Checkout buttons are kept disabled until the cart is confirmed compliant.
  function syncCart() {
    if (syncInFlight) {
      syncQueued = true;
      return syncInFlight;
    }

    toggleCheckoutButtons(true);

    syncInFlight = fetchCart()
      .then((cart) => {
        const { updates } = getFreebieUpdates(cart);
        if (!Object.keys(updates).length) return cart;

        toggleLoadingMessage(true);

        return fetch(`${window.Shopify.routes.root}cart/update.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ updates }),
        })
          .then((res) => {
            if (!res.ok) throw new Error(`Cart update failed (${res.status})`);
            return fetchCart();
          })
          .then((updatedCart) => {
            updateCartSections(updatedCart);
            toggleLoadingMessage(false);
            return updatedCart;
          });
      })
      .then((cart) => {
        const { updates, onlyFreebies, onlyFreebiesAndInsurance } =
          getFreebieUpdates(cart);
        const compliant =
          !Object.keys(updates).length && !hasFreebieOverLimit(cart);

        // Only re-enable checkout when the cart is clean
        toggleCheckoutButtons(
          !compliant || onlyFreebies || onlyFreebiesAndInsurance,
        );
        return cart;
      })
      .catch((err) => {
        console.error("[syncCart] Failed:", err);
        toggleLoadingMessage(false);
        toggleCheckoutButtons(false); // never leave checkout dead on a network error; guard re-checks on click
        return null;
      })
      .finally(() => {
        syncInFlight = null;
        if (syncQueued) {
          syncQueued = false;
          syncCart();
        }
      });

    return syncInFlight;
  }

  // Backwards-compatible name used across this file
  function checkCart() {
    return syncCart();
  }

  function updateCartSections(cart) {
    const mainCartItemsId =
      document.getElementById("main-cart-items")?.dataset.id;
    // console.log("mainCartItemsId", mainCartItemsId);
    const footerSectionId =
      document.getElementById("main-cart-footer")?.dataset.id;

    const sectionQuery = [
      "cart-drawer",
      mainCartItemsId || "main-cart-items",
      "cart-icon-bubble",
      "main-cart-counter",
      footerSectionId || "main-cart-footer",
    ].join(",");

    fetch(`${routes.cart_url}?sections=${sectionQuery}`)
      .then((res) => res.json())
      .then((sections) => {
        const parser = new DOMParser();

        // --- Drawer ---
        const drawerDoc = parser.parseFromString(
          sections["cart-drawer"],
          "text/html",
        );
        const newDrawer = drawerDoc.querySelector("#CartDrawer .drawer__inner");
        const currentDrawer = document.querySelector(
          "#CartDrawer .drawer__inner",
        );
        if (newDrawer && currentDrawer) currentDrawer.replaceWith(newDrawer);

        // --- Main cart items ---
        const mainKey = mainCartItemsId || "main-cart-items";
        const mainDoc = parser.parseFromString(sections[mainKey], "text/html");
        const newMain = mainDoc.querySelector("#main-cart-items");
        const currentMain = document.querySelector("#main-cart-items");
        if (newMain && currentMain) currentMain.replaceWith(newMain);

        // --- Main cart footer ---
        const footerKey = footerSectionId || "main-cart-footer";
        const footerDoc = parser.parseFromString(
          sections[footerKey],
          "text/html",
        );
        const newFooter = footerDoc.querySelector(
          "#main-cart-footer .js-contents",
        );
        const currentFooter = document.querySelector(
          "#main-cart-footer .js-contents",
        );
        if (newFooter && currentFooter) currentFooter.replaceWith(newFooter);

        // --- Cart icon bubble ---
        const bubbleDoc = parser.parseFromString(
          sections["cart-icon-bubble"],
          "text/html",
        );
        const newBubble = bubbleDoc.body.innerHTML.trim();
        const currentBubble = document.querySelector("#cart-icon-bubble");
        if (currentBubble) currentBubble.innerHTML = newBubble;

        // --- Cart counter ---
        const counterDoc = parser.parseFromString(
          sections["main-cart-counter"],
          "text/html",
        );
        const newCounter = counterDoc.querySelector("#main-cart-counter");
        const currentCounter = document.querySelector("#main-cart-counter");
        if (newCounter && currentCounter)
          currentCounter.replaceWith(newCounter);

        // --- Handle empty state like Dawn ---
        const cartDrawerWrapper = document.querySelector("cart-drawer");
        const cartFooter = document.getElementById("main-cart-footer");
        const cartItems = document.querySelector("cart-items");

        if (cartFooter)
          cartFooter.classList.toggle("is-empty", cart.item_count === 0);
        if (cartDrawerWrapper)
          cartDrawerWrapper.classList.toggle("is-empty", cart.item_count === 0);
        if (cartItems)
          cartItems.classList.toggle("is-empty", cart.item_count === 0);

        if (cartItems) {
          const progressBar = cartItems.querySelector(
            ".header-drawer__progress-bar",
          );
          if (progressBar) {
            progressBar.classList.toggle("is-empty", cart.item_count === 0);
          }
        }

        if (cart.item_count === 0 && cartDrawerWrapper) {
          const emptyContent = cartDrawerWrapper.querySelector(
            ".drawer__inner-empty",
          );
          if (emptyContent && typeof trapFocus === "function") {
            trapFocus(emptyContent, emptyContent.querySelector("a"));
          }
        }
      })
      .catch((err) => console.error("[updateCartSections] Failed:", err));
  }

  (function attachAddListeners() {
    const CLICK_DELAY_MS = 250;

    function onAddClick() {
      // Show the loading message early
      toggleLoadingMessage(true);

      // Immediately check current visible cart contents and disable checkout if needed
      checkLocalCartState();

      // Disable checkout generally during async updates
      toggleCheckoutButtons(true);

      // Re-check cart shortly after add/remove request completes
      setTimeout(() => {
        try {
          checkCart();
          // console.log("checkCart() called from add-button hook");
        } catch (e) {
          console.error("checkCart() error from add-button hook:", e);
        }
      }, CLICK_DELAY_MS);
    }

    document.body.addEventListener("click", (e) => {
      const btn = e.target.closest(
        'button[type="submit"][name="add"], button.quick-add__submit, button.product-form__submit, button.cart-remove-button',
      );
      if (btn) onAddClick();
    });

    // Fallback for native form submit (covers forms that use <input type="submit">)
    document.body.addEventListener("submit", (e) => {
      const form = e.target.closest("form[action*='/cart/add']");
      if (form) {
        onAddClick();
      }
    });
  })();

  // --- Guard 1: block any manual add-to-cart of a freebie variant (PDP, quick add, etc.) ---
  function getFormVariantId(form) {
    const input = form?.querySelector('[name="id"]');
    return input ? Number(input.value) : null;
  }

  document.addEventListener(
    "submit",
    (e) => {
      const form = e.target.closest?.("form[action*='/cart/add']");
      if (!form) return;
      if (isFreebie(getFormVariantId(form))) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    },
    true,
  );

  document.addEventListener(
    "click",
    (e) => {
      const btn = e.target.closest?.(
        'button[name="add"], button.product-form__submit, button.quick-add__submit',
      );
      if (!btn) return;
      const form = btn.form || btn.closest("form");
      if (form && isFreebie(getFormVariantId(form))) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    },
    true,
  );

  // --- Guard 2: verify the cart right before checkout ---
  // Stops the "add a 2nd freebie and click Checkout before the auto-fix runs" bypass.
  let checkoutGuardRunning = false;
  const CHECKOUT_SELECTOR =
    '#CartDrawer-Checkout, #checkout, button[name="checkout"], input[name="checkout"], a[href$="/checkout"]';

  function goToCheckout() {
    window.location.href = `${window.Shopify?.routes?.root || "/"}checkout`;
  }

  // Shown when the cart could not be verified: checkout stays blocked instead of failing open
  function showCheckoutError(anchor) {
    let el = document.getElementById("freebie-checkout-error");
    if (!el) {
      el = document.createElement("p");
      el.id = "freebie-checkout-error";
      el.setAttribute("role", "alert");
      el.style.cssText =
        "margin:0 0 1rem;color:var(--red-color,#be1e2d);font-size:0.9em;text-align:center";
      el.textContent =
        "We couldn't confirm your free gift. Please refresh the page and try again.";
    }
    // Cart sections re-render while we verify, which can detach the button that was clicked,
    // so resolve the current checkout button from the live document.
    const target = anchor?.isConnected
      ? anchor
      : (anchor?.id && document.getElementById(anchor.id)) ||
        document.querySelector(CHECKOUT_SELECTOR);
    if (target?.parentNode) target.parentNode.insertBefore(el, target);
  }

  // A cart is ready for checkout when no freebie correction is needed
  function isCheckoutReady(cart) {
    const { updates, onlyFreebies, onlyFreebiesAndInsurance } =
      getFreebieUpdates(cart);
    return (
      !Object.keys(updates).length &&
      !hasFreebieOverLimit(cart) &&
      !onlyFreebies &&
      !onlyFreebiesAndInsurance
    );
  }

  function clearCheckoutError() {
    document.getElementById("freebie-checkout-error")?.remove();
  }

  function guardCheckout(e) {
    e.preventDefault();
    e.stopImmediatePropagation();
    if (checkoutGuardRunning) return;
    checkoutGuardRunning = true;
    clearCheckoutError();

    const anchor =
      e.type === "submit" ? e.submitter : e.target.closest?.(CHECKOUT_SELECTOR);

    flushPendingCartEdits() // wait for debounced quantity / note saves, repeating if the shopper keeps editing
      .then(() => saveUnsavedEdits()) // re-save edits cart.js dropped; throws if a save fails
      .then(() => syncCart())
      .then((cart) => {
        if (!cart) throw new Error("Cart sync failed");
        return fetchCart(); // re-read after any correction
      })
      .then((cart) => {
        if (!cart.items.length) return; // nothing left to check out
        if (isCheckoutReady(cart)) return goToCheckout();

        // Still not clean (e.g. another tab changed the cart): correct once more, then
        // either continue to checkout or report the failure.
        return syncCart()
          .then((updatedCart) => {
            if (!updatedCart) throw new Error("Cart sync failed");
            return fetchCart();
          })
          .then((updatedCart) => {
            if (!updatedCart.items.length) return;
            if (!isCheckoutReady(updatedCart)) {
              throw new Error("Cart is still not eligible for checkout");
            }
            goToCheckout();
          });
      })
      .catch((err) => {
        // Fail closed: never send the shopper to checkout with a cart we could not verify
        console.error("[guardCheckout] Cart could not be verified:", err);
        toggleLoadingMessage(false);
        showCheckoutError(anchor);
      })
      .finally(() => {
        checkoutGuardRunning = false;
      });
  }

  document.addEventListener(
    "click",
    (e) => {
      const btn = e.target.closest?.(CHECKOUT_SELECTOR);
      if (btn) guardCheckout(e);
    },
    true,
  );

  document.addEventListener(
    "submit",
    (e) => {
      if (e.submitter && e.submitter.matches(CHECKOUT_SELECTOR))
        guardCheckout(e);
    },
    true,
  );

  // --- Observe cart DOM for both cart and message updates ---
  const cartContainer = document.querySelector("cart-drawer");
  const cartItems = document.querySelector("cart-items");
  // console.log("[anniversary-freebies] Cart container:", cartContainer);
  // console.log("[anniversary-freebies] Cart items:", cartItems);

  if (cartContainer || cartItems) {
    const observer = new MutationObserver(() => {
      // Recheck cart state when DOM changes
      checkLocalCartState();

      // Check if the loading message appeared dynamically
      const msg = document.getElementById("freebie-loading-message");
      if (msg && msg.classList.contains("hidden") === false) {
        // console.log("[anniversary-freebies] Message element detected");
        toggleLoadingMessage(false); // ensure it's hidden by default
      }

      // console.log("[anniversary-freebies] Observing cart DOM changes");
    });

    if (cartContainer) {
      observer.observe(cartContainer, { childList: true, subtree: true });
    }
    if (cartItems) {
      observer.observe(cartItems, { childList: true, subtree: true });
    }
  }

  // --- Initial check (syncCart re-enables checkout once the cart is confirmed clean) ---
  checkCart();

  // --- Subscribe to cart changes if theme supports it ---
  if (typeof subscribe === "function" && PUB_SUB_EVENTS?.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => checkCart());
  }
})();
// Functionality for Free Gift & Sample Cart Automation for Anniversary Sale end =====
